import { Component, ElementRef, OnInit, ViewChild, signal } from "@angular/core";
import { FormControl } from "@angular/forms";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { debounceTime, filter, map, Observable, startWith } from "rxjs";
import { AuthService } from "../../services/auth/auth.service";
import { UserService } from "../../services/user/user.service";
import { NullableRegisteredUserDetails } from "@models/user.model";
import { LocalStorageService } from "../../services/local-storage/local-storage.service";
import { WishlistService } from "../../services/wishlistService/wishlist.service";

interface NavLink {
    href: string;
    label: string;
}

type SearchContext = "desktop" | "mobile" | null;
@Component({
    selector: "app-header",
    templateUrl: "./header.component.html",
    styleUrls: ["./header.component.scss"],
    standalone: false,
})
export class HeaderComponent implements OnInit {
    public isProfileSidebarOpen = false;
    public isMenuOpen = false;
    public wishlistCount!: number; // Mock wishlist count
    public cartCount!: number; // Mock cart count
    private currentPath = "";
    public isUserLogin!: boolean;
    public userDetails!: NullableRegisteredUserDetails;
    public islogin: boolean = true;
    public isCartOrCheckout: boolean = false;

    // Recent Searches
    public recentSearches: string[] = [];
    public filteredSearches$!: Observable<string[]>;

    // Track dropdown visibility and state
    public searchContext: SearchContext = null;
    public searchControl = new FormControl("");

    // Control flags
    private clearSearchTermOnSubmit = true;
    private suppressDropdownOnFocus = false;

    navLinks: NavLink[] = [
        { href: "/home", label: "Home" },
        { href: "/products", label: "Products" },
        { href: "/designer", label: "Designer" },
        // { href: "/about", label: "About" },s
    ];

    @ViewChild("desktopSearchInput") desktopSearchInput!: ElementRef<HTMLInputElement>;
    @ViewChild("mobileSearchInput") mobileSearchInput!: ElementRef<HTMLInputElement>;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private authService: AuthService,
        private userService: UserService,
        private localStorageService: LocalStorageService,
        private wishlistService: WishlistService,
    ) {}

    ngOnInit() {
        const currentUrl = document.location.href;
        if (currentUrl.includes("cart") || currentUrl.includes("address")) {
            this.isCartOrCheckout = true;
        } else {
            this.isCartOrCheckout = false;
        }
        this.authService.isLoggedIn$.subscribe((loginStatus) => {
            this.isUserLogin = loginStatus;
            if (this.isUserLogin) {
                this.getUserDetails();
            }
            this.subscribeToWishListUpdates();
            this.subscribeToCartUpdates();
            this.subscribeToUserDataChanges();
        });

        this.route.queryParams.subscribe((params) => {
            const searchTerm = params["rawQuery"];
            this.searchControl.setValue(searchTerm);
        });

        this.currentPath = this.router.url;
        if (this.currentPath === "/login") {
            this.islogin = false;
        }

        // Listen to route changes
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
            this.islogin = true;
            this.currentPath = event.urlAfterRedirects;
            if (event.url === "/login") {
                this.islogin = false;
            }

            let currentUrl = event.url;
            if (currentUrl.includes("address") || currentUrl.includes("cart")) {
                this.isCartOrCheckout = true;
            } else {
                this.isCartOrCheckout = false;
            }
        });

        // Load recent searches from localStorage
        const stored = localStorage.getItem("recentSearches");
        this.recentSearches = stored ? JSON.parse(stored) : [];

        // Setup reactive filtering for recent searches
        this.filteredSearches$ = this.searchControl.valueChanges.pipe(
            startWith(""),
            debounceTime(50),
            map((value) => this.filterSearches(value)),
        );
    }

    private getUserDetails(): void {
        this.userService.getUserDetails().subscribe((response) => {
            this.userDetails = response; 
        });
    }

    private filterSearches(value: string | null): string[] {
        if (!value || value.trim() === "") {
            return this.recentSearches;
        }
        const term = value.toLowerCase().trim();
        return this.recentSearches.filter((search) => search.toLowerCase().includes(term));
    }

    // Handle input focus
    public onInputFocus(context: SearchContext) {
        if (this.suppressDropdownOnFocus) {
            this.suppressDropdownOnFocus = false;
            return;
        }

        this.searchContext = context;
    }

    // Handle input changes
    public onInputChange(value: string | null, context: SearchContext) {
        this.setSearchContext(context);
    }

    // Handle search submission
    public handleSearch(term: string | null) {
        const trimmed = (term ?? "").trim();

        // Add to recent searches
        this.addToRecentSearches(trimmed);

        // Close dropdown and blur input
        this.closeSearchDropdown();

        // Get the current category (if any)
        const category = this.route.snapshot.queryParams["category"];

        const queryParams: { rawQuery: string; category?: string } = { rawQuery: trimmed };

        if (category) {
            queryParams.category = category;
        }

        // Navigate to search results
        this.router.navigate(["/products"], { queryParams });
    }

    // Handle recent search selection
    public selectSearch(term: string, context: SearchContext): void {
        // Set the search term
        this.searchControl.setValue(term);

        // Close dropdown
        this.closeSearchDropdown();

        // Suppress dropdown on focus and then focus the input
        this.suppressDropdownOnFocus = true;

        // Focus the appropriate input after a short delay
        setTimeout(() => {
            const input = this.getSearchInput(context);
            if (input) {
                input.focus();
                // Move cursor to end of input
                // input.setSelectionRange(input.value.length, input.value.length);
            }
        }, 0);
    }

    // Close search dropdown
    private closeSearchDropdown() {
        this.searchContext = null;

        // Blur both inputs
        const desktopInput = this.getSearchInput("desktop");
        const mobileInput = this.getSearchInput("mobile");

        if (desktopInput) desktopInput.blur();
        if (mobileInput) mobileInput.blur();
    }

    // Set search context (for click outside directive)
    public setSearchContext(context: SearchContext) {
        this.searchContext = context;
    }

    public handleClickOutside(context: SearchContext) {
        if (context === this.searchContext) {
            this.setSearchContext(null);
        }
    }

    // Add term to recent searches
    public addToRecentSearches(term: string) {
        if (!term) return;
        const lowerTerm = term.toLowerCase();

        // Remove existing match (case-insensitive)
        this.recentSearches = this.recentSearches.filter((search) => search.toLowerCase() !== lowerTerm);

        // Add new term at the beginning
        this.recentSearches.unshift(term);

        // Keep only the most recent 5 searches
        this.recentSearches = this.recentSearches.slice(0, 5);

        // Save to localStorage
        localStorage.setItem("recentSearches", JSON.stringify(this.recentSearches));
    }

    // Get search input element by context
    public getSearchInput(context: SearchContext): HTMLInputElement | null {
        if (context === "desktop" && this.desktopSearchInput) {
            return this.desktopSearchInput.nativeElement;
        } else if (context === "mobile" && this.mobileSearchInput) {
            return this.mobileSearchInput.nativeElement;
        }
        return null;
    }

    // Profile sidebar methods
    public toggleProfileSidebar() {
        this.isProfileSidebarOpen = !this.isProfileSidebarOpen;
    }

    // Mobile menu methods
    public toggleMenu() {
        this.isMenuOpen = !this.isMenuOpen;
    }

    public closeMenu() {
        this.isMenuOpen = false;
    }

    // Navigation methods
    public isActiveLink(href: string): boolean {
        return this.currentPath === href;
    }

    public navigateTo(href: string) {
        this.router.navigate([href]);
        this.closeMenu();
    }

    private subscribeToCartUpdates(): void {
        this.localStorageService.cartUpdateObservable.subscribe(() => {
            this.cartCount = this.getCartItemsLength();
        });
    }

    private subscribeToWishListUpdates(): void {
        this.wishlistService.wishlistCount$.subscribe((response) => {
            this.wishlistCount = response;
        });
    }

    private getCartItemsLength(): number {
        const data = localStorage.getItem("cartItems");
        return data ? JSON.parse(data).length : 0;
    }

    private subscribeToUserDataChanges(): void {
        this.userService.dataSubject.subscribe(() => {
            if (this.authService.isUserLoggedIn) {
                this.getUserDetails();
            }
        });
    }

    public loginDialog(): void {
        this.router.navigate(["/login"]);
    }

    public onUserLogout(): void {
        this.authService.logout();
        this.userDetails = null;
        const channel = new BroadcastChannel("");
        channel.postMessage("userLogout");
        this.isProfileSidebarOpen = !this.isProfileSidebarOpen;
    }
}
